// Konva can't read CSS custom properties; mirror the pallet design tokens
// (packages/@bfc-software/pallet/src/styles/{variables,dark-theme}.css) so
// the canvas stays consistent with the rest of bfc-web-ui.
//
// Theme is read once at render time from `:root.palletThemeDark`. The
// simulator does not live-react to theme toggles after first paint - that
// would require either propagating a MutationObserver subscription down
// into Konva or rebuilding the stage on toggle, which is out of scope.

const DARK_THEME_CLASS = "palletThemeDark";

type PaletteShape = {
  // Borders / fills (slot + trailer cells, OVF bins).
  cellStroke: string; // --pallet-color-gray-200 (warehouse inactive)
  cellStrokeStrong: string; // --pallet-color-gray-300 (trailer cells, OVF bins)
  cellFill: string; // (transparent - represented as undefined consumer-side)
  emptyStroke: string; // --pallet-color-gray-100

  // Active pick highlight (turquoise per spec).
  activeStroke: string; // --pallet-color-turquoise-500
  activeFill: string; // --pallet-color-turquoise-125
  activeStrokeWidth: number;

  // Active-task slots that are not the current pick.
  inTaskStroke: string; // --pallet-color-blue-200

  // Text.
  text: string; // --pallet-color-gray-800
  textMuted: string; // --pallet-color-gray-500
  textTitle: string; // --pallet-color-gray-700

  // Pallet markers (per slot, per forklift position).
  markerActive: string; // --pallet-color-turquoise-500
  markerInTask: string; // --pallet-color-blue-300
  markerUnused: string; // --pallet-color-gray-300
  markerInactive: string; // --pallet-color-gray-200 @ 40% opacity

  // Trailer-cell planner-side bug treatment (col > 2).
  bugStroke: string; // --pallet-color-orange-500
  bugFill: string; // --pallet-color-orange-125

  // Trailer card grid panel when the route has zero picks.
  inactivePanel: string; // --pallet-color-gray-100
};

const LIGHT_PALETTE: PaletteShape = {
  cellStroke: "#e2e2e3", // --pallet-color-gray-200
  cellStrokeStrong: "#cacbcf", // --pallet-color-gray-300
  cellFill: "transparent",
  emptyStroke: "#ededed", // --pallet-color-gray-100

  activeStroke: "#23b1a5", // --pallet-color-turquoise-500
  activeFill: "#ade6e1", // --pallet-color-turquoise-125
  activeStrokeWidth: 2,

  inTaskStroke: "#a2b7e1", // --pallet-color-blue-200

  text: "#282828", // --pallet-color-gray-800
  textMuted: "#7e7c85", // --pallet-color-gray-500
  textTitle: "#414141", // --pallet-color-gray-700

  markerActive: "#23b1a5", // --pallet-color-turquoise-500
  markerInTask: "#3156b4", // --pallet-color-blue-300
  markerUnused: "#cacbcf", // --pallet-color-gray-300
  markerInactive: "#e2e2e3", // --pallet-color-gray-200

  bugStroke: "#d73723", // --pallet-color-orange-500
  bugFill: "#febfb7", // --pallet-color-orange-125

  inactivePanel: "#ededed", // --pallet-color-gray-100
};

const DARK_PALETTE: PaletteShape = {
  cellStroke: "#3a3a3b", // --pallet-color-gray-200 (dark)
  cellStrokeStrong: "#4a4b4f", // --pallet-color-gray-300 (dark)
  cellFill: "transparent",
  emptyStroke: "#2e2e2e", // --pallet-color-gray-100 (dark)

  activeStroke: "#2ec4b7", // --pallet-color-turquoise-500 (dark)
  activeFill: "#1a3d3a", // --pallet-color-turquoise-125 (dark)
  activeStrokeWidth: 2,

  inTaskStroke: "#5b7ec4", // --pallet-color-blue-200 (dark)

  text: "#e8e8e8", // --pallet-color-gray-800 (dark)
  textMuted: "#8e8c95", // --pallet-color-gray-500 (dark)
  textTitle: "#d0d0d0", // --pallet-color-gray-700 (dark)

  markerActive: "#2ec4b7", // --pallet-color-turquoise-500 (dark)
  markerInTask: "#6b8fd4", // --pallet-color-blue-300 (dark)
  markerUnused: "#4a4b4f", // --pallet-color-gray-300 (dark)
  markerInactive: "#3a3a3b", // --pallet-color-gray-200 (dark)

  bugStroke: "#e84d35", // --pallet-color-orange-500 (dark)
  bugFill: "#4a2520", // --pallet-color-orange-125 (dark)

  inactivePanel: "#2e2e2e", // --pallet-color-gray-100 (dark)
};

export const INACTIVE_OPACITY = 0.4;

export function getPalette(): PaletteShape {
  if (
    typeof document !== "undefined" &&
    document.documentElement.classList.contains(DARK_THEME_CLASS)
  ) {
    return DARK_PALETTE;
  }
  return LIGHT_PALETTE;
}

// Kept as a stable default for non-render contexts (e.g. constants in tests).
export const palette = LIGHT_PALETTE;
